import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import {Box, Button, Card, CardContent, CssBaseline, Paper, ThemeProvider, Typography,} from "@mui/material";
import {Footer} from "./elements/footer";
import {FixedHeader} from "./elements/header";
import {muiTheme} from "./theme/theme";

import {useNavigate} from "react-router-dom";
import axios from "axios";
import {useEffect, useState} from "react";
import {skippedSetup,} from "../../api/customerSetup";
import {fetchCurrentProfile} from "../../api/profile";

const steps = [
  {
    title: "Step 1: Set Up",
    icon: (
      <Box
        component="img"
        src="/Healthcare Streamline UX Duotone.svg"
        alt="Set Up"
        sx={{
          width: { xs: 70, sm: 85, md: 99 },
          height: { xs: 70, sm: 85, md: 99 },
          objectFit: "contain",
        }}
      />
    ),
    time: "(Approx. 2mins)",
  },
  {
    title: "Step 2: Link Bank Account",
    icon: (
      <Box
        component="img"
        src="/Link Hand 1 Streamline UX Colors.svg"
        alt="Link Bank Account"
        sx={{
          width: { xs: 70, sm: 85, md: 99 },
          height: { xs: 70, sm: 85, md: 99 },
          objectFit: "contain",
        }}
      />
    ),
    time: "(Approx. 5mins)",
  },
  {
    title: "Step 3: Performance Overview",
    icon: (
      <Box
        component="img"
        src="/Browser Window Check Streamline UX Duotone.svg"
        alt="Performance Overview"
        sx={{
          width: { xs: 70, sm: 85, md: 99 },
          height: { xs: 70, sm: 85, md: 99 },
          objectFit: "contain",
        }}
      />
    ),
    time: "(Approx. 3mins)",
  },
  {
    title: "Step 4: Schedule Intro Call",
    icon: (
      <Box
        component="img"
        src="/book-a-call.svg"
        alt="Schedule Intro Call"
        sx={{
          width: { xs: 70, sm: 85, md: 99 },
          height: { xs: 70, sm: 85, md: 99 },
          objectFit: "contain",
        }}
      />
    ),
    time: "(Approx. 2mins)",
  },
];

const SetupPrompt = ({ profile, setProfile }) => {
  const navigate = useNavigate();
  const [profileData, setProfileData] = useState({});
  const [isSkipping, setIsSkipping] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    let isActive = true;

    const refreshProfileData = async (id) => {
      if (id) {
        try {
          const res = await axios.get(`/api/v2/profiles/${id}/`, {
            headers: { "Content-Type": "application/json" },
            withCredentials: true,
            signal: controller.signal,
          });
          if (isActive) {
            setProfileData(res.data);
          }
        } catch (err) {
          if (err?.response) console.log(err);
        }
      }
    };

    refreshProfileData(profile.id);

    return () => {
      isActive = false;
      controller.abort();
    };
  }, [profile]);

  return (
    <ThemeProvider theme={muiTheme}>
      <CssBaseline />
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          fontFamily: "Rethink Sans",
          width: "100%",
          background:
            "linear-gradient(81.17deg, #121621 -27.35%, #536DEC 142.61%)",
        }}
      >
        <FixedHeader />

        <Box
          sx={{
            ml: "20px !important",
            mr: "20px !important",
            width: "100%",
            flex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            gap: "24px",
            mt: "100px",
            pb: "35px",
          }}
        >
          <Box
            minHeight={"80vh"}
            sx={{
              minHeight: "80vh",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              px: { xs: 2, sm: 4 },
            }}
          >
            <Paper
              elevation={1}
              sx={{
                maxWidth: "1100px",
                borderRadius: "16px",
                border: "1px solid #21212114",
                boxShadow: "0px 2px 4px 0px #00000013",
                p: { xs: 2.5, sm: 4, md: 6 },
                mx: "auto",

                px: { xs: 2, sm: 4, md: 6 },
                width: {
                  xs: "calc(100% - 50px)",
                  sm: "calc(100% - 50px)",
                  md: "100%",
                },
              }}
            >
              {/* Header */}
              <Box sx={{ textAlign: "center" }}>
                <Typography
                  fontFamily="Rethink Sans"
                  fontWeight={600}
                  fontSize={{ xs: "22px", sm: "26px", md: "28px" }}
                  lineHeight="117%"
                  color="#000000DE"
                >
                  {`Welcome to Lucro, ${profileData?.user?.first_name || ""}!`}
                </Typography>

                <Typography
                  mt={1.5}
                  fontFamily="Rethink Sans"
                  fontSize={{ xs: "14px", sm: "15px", md: "16px" }}
                  lineHeight="150%"
                  letterSpacing="0.15px"
                  color="#00000099"
                >
                  Let’s get your account set up so you can start growing your
                  business with clarity and confidence.
                </Typography>
              </Box>

              {/* Steps */}
              <Box
                sx={{
                  mt: "40px",
                  width: "100%",
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "repeat(2, minmax(0, 1fr))",
                    md: "repeat(4, minmax(0, 1fr))",
                  },
                  gap: 2,
                  alignItems: "stretch",
                }}
              >
                {steps.map((step, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: "flex",
                      minWidth: 0,
                      minHeight: { xs: "187px", md: "220px" },
                    }}
                  >
                    <Card
                      sx={{
                        width: "100%",
                        height: "100%",
                        minHeight: "187px",
                        minWidth: 0,
                        flex: 1,
                        borderRadius: "6.66px",
                        borderWidth: "1px",
                        border: "1px solid #12162114",
                        boxShadow: "0px 0px 2.61px 0px #0000001F",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        textAlign: "center",
                      }}
                    >
                      <CardContent
                        sx={{ p: 2, width: "100%", "&:last-child": { pb: 2 } }}
                      >
                        <Box
                          display="flex"
                          flexDirection="column"
                          alignItems="center"
                          gap={1}
                        >
                          <Typography
                            fontFamily="Rethink Sans"
                            fontWeight="600"
                            fontSize={{ xs: "14px", sm: "16px" }}
                            lineHeight="133%"
                            letterSpacing="0px"
                            textAlign="center"
                            sx={{
                              color: "#000000DE",
                              wordBreak: "break-word",
                              overflowWrap: "break-word",
                            }}
                          >
                            {step.title}
                          </Typography>

                          {step.icon}

                          <Typography
                            fontFamily="Rethink Sans"
                            fontSize="13px"
                            lineHeight="150%"
                            letterSpacing="0.1px"
                            textAlign="center"
                            sx={{ color: "#00000099" }}
                          >
                            {step.time}
                          </Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  </Box>
                ))}
              </Box>

              {/* Bottom Buttons */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  justifyContent: "space-between",
                  alignItems: "center",
                  mt: { xs: 3, sm: 4 },
                  gap: { xs: 2, sm: 0 },
                }}
              >
                <Button
                  startIcon={<ArrowBackIosNewIcon fontSize="small" />}
                  sx={{
                    color: muiTheme.palette.primary.main,
                    textTransform: "none",
                    fontWeight: 600,
                    alignSelf: { xs: "center", sm: "flex-start" },
                  }}
                  onClick={() => navigate("/logout")}
                >
                  Return To Login
                </Button>

                <Box
                  sx={{
                    display: "flex",
                    gap: 2,
                    flexDirection: { xs: "column", sm: "row" },
                    width: { xs: "100%", sm: "auto" },
                  }}
                >
                  <Button
                    variant="outlined"
                    disabled={isSkipping}
                    sx={{
                      textTransform: "none",
                      borderRadius: "4px",
                      width: { xs: "100%", sm: "auto" },
                    }}
                    onClick={async () => {
                      if (isSkipping) return;

                      try {
                        setIsSkipping(true);

                        sessionStorage.setItem("demo_onboarding_bypass", "true");

                        await skippedSetup();

                        const [currentProfile] = await fetchCurrentProfile();
                        setProfile(currentProfile);

                        navigate("/");
                      } catch (error) {
                        console.error("Error skipping setup:", error);
                      } finally {
                        setIsSkipping(false);
                      }
                    }}
                  >
                    {isSkipping ? "Loading..." : "Skip (View Demo)"}
                  </Button>
                  <Button
                    variant="contained"
                    sx={{
                      textTransform: "none",
                      borderRadius: "4px",
                      width: { xs: "100%", sm: "auto" },
                      backgroundColor: muiTheme.palette.primary.main,
                      "&:hover": { backgroundColor: "#4C63D7" },
                      boxShadow: "0px 6px 8px -2px #00000033",
                    }}
                    onClick={() => {
                      localStorage.setItem("setup_stepper_step", 0);
                      navigate("/setup");
                    }}
                  >
                    Proceed with Set Up
                  </Button>
                </Box>
              </Box>
            </Paper>
          </Box>

          <Footer />
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default SetupPrompt;
